import { Send, Instagram, ArrowUpRight, LineChart, Wallet } from "lucide-react";
import { BRAND } from "./data";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 hs-grid-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 hs-radial-glow pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <div
              className="hs-fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur"
              data-testid="hero-eyebrow"
            >
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-[#00FF66] hs-pulse-dot" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF66]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-neutral-300">
                Edukacja · analiza rynku · społeczność
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="hs-fade-up hs-fade-up-delay-1 font-display font-bold tracking-tighter mt-6 text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]"
            >
              Zrozum rynek.
              <br />
              <span className="text-[#00FF66] hs-glow-text">Działaj świadomie.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="hs-fade-up hs-fade-up-delay-2 mt-7 text-lg md:text-xl text-neutral-400 max-w-xl leading-relaxed"
            >
              Analizy rynku, omówienia wykresów i sygnały tradingowe w jednym miejscu.
              Poznaj kontekst rynkowy, obserwuj kluczowe poziomy i ucz się podejmować
              bardziej świadome decyzje.
            </p>

            <div className="hs-fade-up hs-fade-up-delay-3 mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={BRAND.telegram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-telegram-cta"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-[#00FF66] text-black font-bold text-base hover:bg-[#00CC52] transition-all hover:scale-[1.02] active:scale-[0.98] hs-button-glow"
              >
                <Send className="h-5 w-5" />
                Dołącz do Telegrama
                <ArrowUpRight className="h-4 w-4 opacity-70" />
              </a>
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-instagram-cta"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-white/[0.04] border border-white/10 text-white font-medium text-base hover:bg-white/[0.08] hover:border-white/20 transition"
              >
                <Instagram className="h-5 w-5" />
                Poznaj nas na Instagramie
              </a>
              <a
                href={BRAND.okx}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-okx-cta"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-white/[0.04] border border-white/10 text-white font-medium text-base hover:bg-white/[0.08] hover:border-white/20 transition"
              >
                <Wallet className="h-5 w-5" />
                Giełda, z której korzystam
              </a>
            </div>

            <p
              data-testid="hero-community-note"
              className="hs-fade-up hs-fade-up-delay-4 mt-10 text-sm text-neutral-500 max-w-xl"
            >
              <span className="text-white font-semibold">900+ osób</span> w społeczności
              HypeSignals_4u — codzienne omówienia rynku i dyskusje w zamkniętej grupie.
            </p>
          </div>

          {/* Right visual: analytical dashboard mockup */}
          <div className="lg:col-span-5 hs-fade-up hs-fade-up-delay-3">
            <AnalysisMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function AnalysisMockup() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-[#00FF66]/10 blur-3xl rounded-[2rem] pointer-events-none" />

      <div
        className="relative rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#0d0d0d] to-[#080808] p-5 shadow-2xl"
        data-testid="analysis-mockup"
      >
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[#00FF66]/15 border border-[#00FF66]/30 flex items-center justify-center">
              <LineChart className="h-4 w-4 text-[#00FF66]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                Analiza rynku · przykład
              </p>
              <p className="text-white font-display font-semibold text-sm tracking-tight">
                BTC/USDT · struktura 4H
              </p>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.04] text-neutral-300 text-[10px] font-semibold uppercase tracking-wider border border-white/[0.08]"
            data-testid="mockup-status-badge"
          >
            W obserwacji
          </span>
        </div>

        {/* Mini chart with levels */}
        <div className="mt-5 h-44 relative rounded-xl bg-[#050505] border border-white/[0.05] overflow-hidden">
          <svg
            viewBox="0 0 400 180"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full"
          >
            <defs>
              <linearGradient id="hsline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00FF66" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#00FF66" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[30, 70, 110, 150].map((y) => (
              <line
                key={y}
                x1="0"
                y1={y}
                x2="400"
                y2={y}
                stroke="rgba(255,255,255,0.04)"
                strokeWidth="1"
              />
            ))}
            {/* Key level (resistance) */}
            <line
              x1="0"
              y1="46"
              x2="400"
              y2="46"
              stroke="#00FF66"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.6"
            />
            {/* Invalidation level */}
            <line
              x1="0"
              y1="150"
              x2="400"
              y2="150"
              stroke="#ff5a5f"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.5"
            />
            <path
              d="M0,140 L30,132 L60,136 L90,122 L120,128 L150,110 L180,114 L210,92 L240,98 L270,76 L300,82 L330,62 L360,70 L400,54"
              fill="none"
              stroke="#e5e5e5"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
            />
            <path
              d="M0,140 L30,132 L60,136 L90,122 L120,128 L150,110 L180,114 L210,92 L240,98 L270,76 L300,82 L330,62 L360,70 L400,54 L400,180 L0,180 Z"
              fill="url(#hsline)"
            />
          </svg>
          <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
            4H · przykład poglądowy
          </div>
          <div className="absolute top-6 right-3 text-[10px] text-[#00FF66] font-semibold">
            — kluczowy opór
          </div>
          <div className="absolute bottom-7 right-3 text-[10px] text-red-400 font-semibold">
            — unieważnienie
          </div>
        </div>

        {/* Analysis grid */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          {[
            {
              l: "Obserwowany scenariusz",
              v: "Reakcja na opór",
            },
            {
              l: "Kluczowy poziom",
              v: "Krawędź konsolidacji",
            },
            {
              l: "Potencjalna strefa reakcji",
              v: "Wyznaczona na 4H",
            },
            {
              l: "Poziom unieważnienia",
              v: "Zamknięcie poniżej wsparcia",
              c: "text-red-300",
            },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                {s.l}
              </p>
              <p className={`mt-1 font-display font-medium text-[13px] ${s.c || "text-white"}`}>
                {s.v}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
            Zarządzanie ryzykiem
          </p>
          <p className="mt-1 text-neutral-300 text-[13px] leading-snug">
            Rozmiar pozycji dostosowany do akceptowalnej straty, jasno zdefiniowany poziom
            unieważnienia scenariusza.
          </p>
        </div>

        <p
          className="mt-4 text-[11px] text-neutral-600 leading-relaxed"
          data-testid="mockup-disclaimer"
        >
          Przykład poglądowy — nie stanowi rekomendacji inwestycyjnej ani odzwierciedlenia
          historycznych wyników.
        </p>
      </div>
    </div>
  );
}
